import { AuthToken, DeviceAccessor, DynamicLoader, LogManager, RegisteredRoutes, ServiceAddresses } from "@mcleod/core";
import { MainPage, MainPageProps } from "@mcleod/common";
import { Layout, Panel, ScreenStack } from "@mcleod/components";
import { AppPageHeader } from "../pageheader/AppPageHeader";
import { AppRouter } from "../router/AppRouter";
import { AppPageFooter } from "../pagefooter/AppPageFooter";
import { AppMainPageProps } from "./AppMainPageProps";
import { AppConnectionErrorLayout } from "./AppConnectionErrorLayout";
import { MobileDeviceCameraAccessor } from "../camera";
import { VirtualKeyboardDisplayHandler } from "../keyboard";
import { MobileDeviceOrientationAccessor } from "../orientation";
import { AppSettingsAccessor } from "../app";
import { MobileDeviceAccessor, SystemBarsAccessor } from "../device";
import { SplashAccessor } from "../splash";
import { CapacitorAccess } from "../capacitor";
import { AppBackButtonHandler } from "../navigation";

const log = LogManager.getLogger("common.AppMainPage");

const beforeUnauthLookup = async () => {
    const res = await AppSettingsAccessor.instance.getConnectionUrl();
    if (res?.url) {
        const url = new URL(res.url);
        const port = Number(url.port);
        if (port > 0) {
            ServiceAddresses.setLocalHostServerPort(port);
        }
        ServiceAddresses.setDefaultAddress('mcleod-mobile-origin', url.origin);
    }
};

export class AppMainPage extends MainPage implements AppMainPageProps {
    private _shouldHideFooter: boolean;

    constructor(props: Partial<AppMainPageProps>) {
        super({ ...props, onError: props?.onError ?? AppMainPage.showConnectionError });
        DynamicLoader.mapPathsToClasses("mobile-components", require['context']("@mcleod/mobile-components/src", true));

        this.provideAppSpecificConfiguration();
        this.registerReturnToShellOnLogout();
    }

    protected override searchUnauthSettings(props: Partial<MainPageProps>) {
        beforeUnauthLookup().then(() => super.searchUnauthSettings(props));
    }

    private provideAppSpecificConfiguration(): void {
        const style = document.createElement('style');
        style.textContent = `* { -webkit-tap-highlight-color: transparent; }`;
        document.head.appendChild(style);

        DeviceAccessor.device = MobileDeviceAccessor.instance;
        DeviceAccessor.cameraAccessor = MobileDeviceCameraAccessor.instance;
        DeviceAccessor.orientationAccessor = MobileDeviceOrientationAccessor.instance;
        VirtualKeyboardDisplayHandler.initialize();
        AppBackButtonHandler.initialize();

        // Whatever sits under the OS bars changes with every route, overlay and footer change; keep the bar icon
        // colors in step.  Overlays animate in over ~300ms, so a second pass follows the immediate one.
        ScreenStack.addListener(() => this.syncSystemBars());
        this.router.addRouteChangeListener(() => this.syncSystemBars());
    }

    /**
     * When the auth token is cleared return to mobile app sign-in shell
     * by asking the native OTA plugin to reload the built-in bundle.
     *
     * This is a no-op unless running mobile app with the OTA plugin present.
     * The plugin itself only reloads when displaying an OTA bundle, so it is
     * safe for every AppMainPage-based app (browser/web and non-OTA native builds
     * simply ignore it).
     */
    private registerReturnToShellOnLogout(): void {
        if (!CapacitorAccess.isNativePlatform) {
            return;
        }
        AuthToken.addLoginListener((isAuthenticated: boolean) => {
            if (isAuthenticated) {
                return;
            }
            CapacitorAccess.plugins?.OTAPlugin?.reloadBuiltIn?.().catch(() => {
                // OTA plugin (or reloadBuiltIn) not available in this build — ignore.
            });
        });
    }

    /**
     * Default handler for the unauthenticated-settings lookup failing (server down, device offline, wrong URL).
     * Replaces the desktop static error page, which has no controls, with a mobile layout that offers a retry and
     * (when running inside the OTA shell) a way back to the environment list.  Also makes sure the native splash
     * screen is dismissed, since the normal dismissal happens only after successful initial routing.
     */
    private static showConnectionError(mainPage: MainPage, error: unknown): void {
        log.error("Unable to load unauthenticated settings", error);
        mainPage.removeAll();
        const errorLayout = new AppConnectionErrorLayout();
        mainPage.add(errorLayout);
        // The shell that loaded us may have left the bars styled for a dark page.
        (mainPage as AppMainPage).syncSystemBars?.();
        SplashAccessor.instance?.hide({ fadeOutDuration: 200 });
    }

    public syncPageTopPadding() {
        const hasHeader = this.pageHeader && this.contains(this.pageHeader);

        this.pageHeader.style.paddingTop = hasHeader ? "var(--safe-area-inset-top, env(safe-area-inset-top, 0px))" : "0px";
        this.router.style.paddingTop = hasHeader ? "0px" : "var(--safe-area-inset-top, env(safe-area-inset-top, 0px))";
    }

    public syncPageBottomPadding() {
        const hasVisibleFooter = this.pageFooter
            && this.pageFooter.components.length > 0
            && this.contains(this.pageFooter);

        this.pageFooter.style.paddingBottom = hasVisibleFooter ? "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))" : "0px";
        this.router.style.paddingBottom = hasVisibleFooter ? "0px" : "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))";
    }

    private _systemBarsFollowUp: ReturnType<typeof setTimeout> | null = null;

    /**
     * Chooses light or dark icons for the status bar from what is painted at the top edge of the screen and for the
     * Android navigation bar from what is painted at the bottom edge (page footer, slideout, sheet, ...).  Runs once
     * now and once more after the usual slide/fade animations have finished.
     */
    public syncSystemBars(): void {
        if (!SystemBarsAccessor.isAvailable)
            return;
        SystemBarsAccessor.syncToScreenEdges();
        if (this._systemBarsFollowUp != null)
            clearTimeout(this._systemBarsFollowUp);
        this._systemBarsFollowUp = setTimeout(() => {
            this._systemBarsFollowUp = null;
            SystemBarsAccessor.syncToScreenEdges();
        }, 400);
    }

    public override get pageHeader(): AppPageHeader {
        return AppPageHeader.instance;
    }

    public override get router(): AppRouter {
        return AppRouter.instance;
    }

    public get pageFooter(): AppPageFooter {
        return AppPageFooter.instance;
    }

    public override evaluateExternalLinkVisibility(topMostPanel: Panel, currentLayouts: Layout[]) {
        // TODO I envision help link button living on the AppPageFooter object, once we have an fleshed out implementation.
    }

    override performInitialRouting() {
        super.performInitialRouting();
        this.syncFooter();
        this.syncPageTopPadding();
        this.syncSystemBars();
        SplashAccessor.instance?.hide({ fadeOutDuration: 200 });
    }

    public syncFooter() {
        const footer = this.pageFooter;
        const footerAlreadyPresent = this.contains(footer);
        const shouldShowFooter = this.isPageWithFooter(this.router._route);

        if (shouldShowFooter && !footerAlreadyPresent)
            this.add(footer);
        else if (!shouldShowFooter && footerAlreadyPresent)
            this.remove(footer);
        else if (shouldShowFooter && footerAlreadyPresent) {
            this.remove(footer);
            this.insert(footer, this.components.length);
        }
        this.syncPageBottomPadding();
    }

    private isPageWithFooter(page: string): boolean {
        return !this.shouldHideFooter
            && AuthToken.isAuthenticated()
            && RegisteredRoutes.hasPageHeader(page);
    }

    public get shouldHideFooter(): boolean {
        return this._shouldHideFooter ?? true;
    }

    public set shouldHideFooter(value: boolean) {
        this._shouldHideFooter = value;
        this.syncFooter();
    }

    public static get instance(): AppMainPage {
        return MainPage.instance as AppMainPage;
    }
}
